// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Shared photoperiod parsing + timezone-aware day/night span helpers for Aura (AROYA).

import { addDays } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

export interface Photoperiod {
  on: string;
  off: string;
}

const DEFAULT_PHOTOPERIOD: Photoperiod = { on: "08:00", off: "20:00" };

/** Normalize "6:00" / "06:00:00" → "06:00" for <input type="time">. */
export function normalizeTimeInput(value: string): string {
  const parts = value.trim().split(":");
  if (parts.length < 2) return value.slice(0, 5);
  const h = parts[0].padStart(2, "0");
  const m = parts[1].padStart(2, "0");
  return `${h}:${m}`;
}

/**
 * Defensive read of rooms.photoperiod jsonb → { on, off } as "HH:MM".
 * Handles {on,off}, {value:"HH:MM-HH:MM"}, bare strings, and legacy key names.
 */
export function readPhotoperiod(p: unknown): Photoperiod {
  if (!p) return { ...DEFAULT_PHOTOPERIOD };

  if (typeof p === "string") {
    if (p.includes("-")) {
      const [on, off] = p.split("-");
      return { on: normalizeTimeInput(on), off: normalizeTimeInput(off) };
    }
    return { ...DEFAULT_PHOTOPERIOD };
  }

  if (typeof p === "object" && p !== null) {
    const o = p as Record<string, unknown>;
    const on = o.on ?? o.lights_on ?? o.lightsOn ?? o.on_time;
    const off = o.off ?? o.lights_off ?? o.lightsOff ?? o.off_time;
    if (typeof on === "string" && typeof off === "string") {
      return { on: normalizeTimeInput(on), off: normalizeTimeInput(off) };
    }
    if (typeof o.value === "string" && o.value.includes("-")) {
      const [a, b] = o.value.split("-");
      return { on: normalizeTimeInput(a), off: normalizeTimeInput(b) };
    }
  }

  return { ...DEFAULT_PHOTOPERIOD };
}

export function formatPhotoperiodLabel(p: unknown): string {
  const { on, off } = readPhotoperiod(p);
  return `${on}–${off}`;
}

function parseHm(hm: string): { h: number; m: number } {
  const [h, m] = hm.split(":").map(Number);
  return { h: h || 0, m: m || 0 };
}

function hmToMinutes(hm: string): number {
  const { h, m } = parseHm(hm);
  return h * 60 + m;
}

/** Local date string YYYY-MM-DD for `ms` in `timeZone`. */
export function localDateKey(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(ms);
}

/** Epoch ms for a wall-clock `{date}T{hm}` interpreted in `timeZone`. */
export function localWallToUtcMs(date: string, hm: string, timeZone: string): number {
  const { h, m } = parseHm(hm);
  const stamp = `${date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
  return fromZonedTime(stamp, timeZone).getTime();
}

/** Format epoch ms as HH:MM in the facility timezone (for chart axes). */
export function formatChartTime(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(ms);
}

/** Multi-day charts: include date on the axis. */
export function formatChartAxisTime(ms: number, timeZone: string, multiDay: boolean): string {
  if (!multiDay) return formatChartTime(ms, timeZone);
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(ms);
}

function nextLocalDateKey(dateKey: string, timeZone: string): string {
  const utc = fromZonedTime(`${dateKey}T12:00:00`, timeZone);
  return formatInTimeZone(addDays(toZonedTime(utc, timeZone), 1), timeZone, "yyyy-MM-dd");
}

function prevLocalDateKey(dateKey: string, timeZone: string): string {
  const utc = fromZonedTime(`${dateKey}T12:00:00`, timeZone);
  return formatInTimeZone(addDays(toZonedTime(utc, timeZone), -1), timeZone, "yyyy-MM-dd");
}

/**
 * Night (lights-off) spans within [from, to] for chart shading.
 * Photoperiod on/off are wall-clock times in `timeZone`.
 */
export function nightSpans(
  pp: Photoperiod | null,
  from: number,
  to: number,
  timeZone = "UTC",
): { x1: number; x2: number }[] {
  if (!pp || to <= from) return [];

  const onMin = hmToMinutes(pp.on);
  const offMin = hmToMinutes(pp.off);
  const spans: { x1: number; x2: number }[] = [];

  let dayKey = prevLocalDateKey(localDateKey(from, timeZone), timeZone);
  const endKey = localDateKey(to, timeZone);

  while (dayKey <= endKey) {
    const dayStart = localWallToUtcMs(dayKey, "00:00", timeZone);
    const dayEnd = localWallToUtcMs(nextLocalDateKey(dayKey, timeZone), "00:00", timeZone);
    const onMs = localWallToUtcMs(dayKey, pp.on, timeZone);
    const offMs = localWallToUtcMs(dayKey, pp.off, timeZone);

    if (onMin <= offMin) {
      // Standard (e.g. 06:00–18:00): lights off = [midnight→on) ∪ [off→midnight).
      pushSpan(spans, dayStart, onMs, from, to);
      pushSpan(spans, offMs, dayEnd, from, to);
    } else {
      // Overnight lights-on (e.g. 20:00–08:00): lights off = [off→on) same local day.
      pushSpan(spans, offMs, onMs, from, to);
    }

    dayKey = nextLocalDateKey(dayKey, timeZone);
  }

  return spans;
}

function pushSpan(
  spans: { x1: number; x2: number }[],
  start: number,
  end: number,
  from: number,
  to: number,
) {
  const x1 = Math.max(start, from);
  const x2 = Math.min(end, to);
  if (x2 > x1) spans.push({ x1, x2 });
}

/** Browser-detected IANA timezone as a sensible default for US facilities. */
export function defaultFacilityTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export const FACILITY_TIMEZONE_OPTIONS = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
] as const;
